import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { ButtonIcon } from "../../../common/components/Button";
import Navigation from "../../../common/components/Navigation/Navigation";
import { openSlider } from "../../../common/state/slice/modal/modalSlice";
import { RootState } from "../../../common/state/store";
import { setAlert } from "../../alert/state/alertsSlice";
import { getNodeById } from "../../graph/state/graphLegacy";
import {
  getMediaSearch,
  getCurrentModeListGallery,
  isUploading,
  setCurrentModeListGallery,
  setUploadedFile,
} from "../state/gallerySlice";
import {
  computeUploadPanelParams,
  deduceMediaType,
  fromMediaTypeInferInputAcceptParam,
} from "../state/uploadUtil";
import { GENERATE_MEDIA_SLIDEOVER_COMPONENT } from "./GenerateMediaPanel";
import { MediaSearchPanelContainer } from "./MediaSearchPanel";
import { useLocation } from "react-router-dom";
import Alert from "../../../common/components/Alert/Alert";
import { ViewGridIcon, ViewListIcon } from "@heroicons/react/outline";
import { isStringNullOrEmpty } from "src/common/util/isStringNullOrEmpty";
import { useIsModalGallery } from "src/common/util/isModalGallery";
import { getCurrentUser } from "src/features/profile/state/profileSlice";
import { isGifMedia } from "../util/mediaTypeUtil";

export const regexIsNodeContentScene = (pathname: string) => {
  return /courses\/\d+\/list\/\d+\/graph\/create\/scene/.test(pathname);
};

export const Nav = ({
  nodeId,
  title = "",
  allowedMediaTypeOverride,
}: {
  nodeId: string;
  title?: string;
  allowedMediaTypeOverride?: string;
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const isModalGallery = useIsModalGallery();

  const currentMediaFilter = useSelector(getMediaSearch) as any;
  const uploading = useSelector(isUploading);
  const currentUser = useSelector(getCurrentUser);
  const currentModeListGallery = useSelector(getCurrentModeListGallery);

  const hiddenFileInput = useRef(null);
  const [inputKey, setInputKey] = useState(Date.now());

  const currentNode =
    Number(nodeId) !== 0 ? useSelector((state: RootState) => getNodeById(state, nodeId)) : null;

  const isNodeContentScene = currentNode?.modelName === "content-scene";

  let fileType = "all";
  if (currentNode) {
    if (currentNode.modelName === "content-scene" && currentMediaFilter?.mediaType !== "model3d") {
      fileType = "content-scene";
    } else {
      fileType = currentMediaFilter?.mediaType;
    }
  }
  if (regexIsNodeContentScene(pathname)) {
    fileType = "content-scene";
  }
  const alertExtension = (e: any, isGif = false) => {
    e.target.value = null;
    dispatch(
      setAlert({
        msg: isGif ? t("general.extensionNotAcceptedGif") : t("general.extensionNotAccepted"),
        type: "danger",
        isOpen: true,
      }),
    );
  };

  const isNodeContentSceneOrRegex = isNodeContentScene || regexIsNodeContentScene(pathname);

  return (
    <>
      {isNodeContentSceneOrRegex && (
        <div className="z-50 text-center">
          <Alert />
        </div>
      )}
      <Navigation
        title={title}
        buttons={
          <>
            {currentUser?.role?.name === "Creator" && (
              <>
                <ButtonIcon
                  styled="btn-primary-fill w-full"
                  text={t("pages.project.importMedia")}
                  icon="CloudUploadIcon"
                  onClick={() => {
                    const hfi = hiddenFileInput?.current as any;
                    hfi.click();
                  }}
                  disabled={uploading}
                />
                <input
                  id="upload-input"
                  key={inputKey}
                  type="file"
                  ref={hiddenFileInput}
                  style={{ display: "none" }}
                  accept={fromMediaTypeInferInputAcceptParam(fileType) ?? "image"}
                  onChange={(e: any) => {
                    const file = e.target.files[0];
                    const extension = file?.name.slice(file?.name.lastIndexOf("."));

                    // Use isGifMedia helper for consistent GIF detection across all upload entry points
                    const testIfExtensionIsGif = () => {
                      if (isNodeContentSceneOrRegex) {
                        return isGifMedia({ type: file?.type, filename: file?.name });
                      }
                      return false;
                    };

                    let currentFileType = e.target.files[0].type;
                    if (isStringNullOrEmpty(currentFileType)) {
                      const splitFileName = e.target.files[0].name.split(".");
                      if (splitFileName.length > 0) {
                        try {
                          const type = deduceMediaType(splitFileName[splitFileName.length - 1]);
                          currentFileType = type + "/" + splitFileName[splitFileName.length - 1];
                        } catch {
                          currentFileType = "";
                        }
                      }
                    }

                    const splitInArray = fromMediaTypeInferInputAcceptParam(fileType).split(",");
                    const testExtensions = !splitInArray.includes(currentFileType);

                    if (testExtensions || extension === ".m4v" || testIfExtensionIsGif()) {
                      alertExtension(e, testIfExtensionIsGif());
                    } else {
                      const uploadedFile = e.target.files[0] as File;
                      const cupp = computeUploadPanelParams({ file: uploadedFile, from: "graph" });
                      dispatch(setUploadedFile({ file: uploadedFile, metadata: cupp }));
                      setInputKey(Date.now());
                    }
                  }}
                ></input>
              </>
            )}

            {currentUser?.role?.name === "Creator" && (
              <ButtonIcon
                styled="btn-alternative-outline w-full mt-[0.4rem] mb-[0.4rem]"
                text={t("pages.gallery.generateMediaPanel.title")}
                icon="PuzzleIcon"
                onClick={() => {
                  dispatch(openSlider(GENERATE_MEDIA_SLIDEOVER_COMPONENT));
                }}
                disabled={uploading}
              />
            )}
            {!isModalGallery && (
              <button
                className="w-7 flex justify-end mb-1 ml-44"
                onClick={() => dispatch(setCurrentModeListGallery(!currentModeListGallery))}
              >
                {currentModeListGallery ? (
                  <ViewGridIcon className="w-7 h-7 stroke-green-500 " />
                ) : (
                  <ViewListIcon className="w-7 h-7 stroke-green-500 " />
                )}
              </button>
            )}
          </>
        }
        tagsBar={
          <MediaSearchPanelContainer
            nodeId={nodeId}
            allowedMediaTypeOverride={allowedMediaTypeOverride}
          />
        }
      />
    </>
  );
};
